/**
 * Shared building blocks of the C2 contract (specs/c2-kontratlar.md 2.1 and 2.4.1):
 * identifiers, time and date strings, hashes, channels, agent ids and reason codes.
 *
 * Database bigint ids travel as decimal digit strings ("214"). Instants in the panel
 * view model are epoch milliseconds; resource endpoints use ISO 8601 with an offset.
 */
import { z } from 'zod';
/** A database bigint id as decimal digits, no leading zero. */
export const ID_PATTERN = /^[1-9]\d{0,17}$/;
export const idStringSchema = z
    .string()
    .regex(ID_PATTERN, { error: 'id must be a positive integer as decimal digits' });
/** ISO 8601 with an offset or Z, for example 2026-09-27T07:41:12.000+03:00. */
export const isoDateTimeSchema = z.iso.datetime({ offset: true });
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** True when value is YYYY-MM-DD and names a real calendar day. */
export function isIsoDate(value) {
    const match = ISO_DATE.exec(value);
    if (!match)
        return false;
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}
/** A calendar day, read as a Europe/Istanbul day. */
export const isoDateSchema = z.string().refine(isIsoDate, { error: 'date must be a real YYYY-MM-DD day' });
export const SHA256_PATTERN = /^[0-9a-f]{64}$/;
export const sha256Schema = z.string().regex(SHA256_PATTERN, { error: 'expected 64 lowercase hex characters' });
/** Epoch milliseconds (panel view model). */
export const instantSchema = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
/** Integer micro US dollars (1 USD = 1_000_000). */
export const microUsdSchema = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
/** A non-negative count. */
export const countSchema = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
/** A database id that the panel view model carries as a number (runId); the API checks Number.isSafeInteger. */
export const numericIdSchema = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
export const channelSchema = z.enum(['panel', 'telegram']);
/** Telegram user id of the human who decided (the bot limits it to TELEGRAM_ALLOWED_USER_ID). */
export const telegramUserIdSchema = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
/** Agent id as config/agents.yaml and twin.agents write it (twin.agents CHECK). */
export const CONFIG_AGENT_ID_PATTERN = /^[a-z][a-z0-9-]{1,39}$/;
export const configAgentIdSchema = z.string().regex(CONFIG_AGENT_ID_PATTERN, { error: 'expected a config agent id' });
export const PANEL_AGENT_ID_VALUES = ['gm', 'temsilci', 'icerik', 'denetci'];
export const panelAgentIdSchema = z.enum(PANEL_AGENT_ID_VALUES);
/**
 * The only mapping between config agent ids and panel agent ids. An agent that is not
 * here (the growth agents) is not shown in the C2 panel.
 */
export const PANEL_AGENT_IDS = Object.freeze({
    'genel-mudur': 'gm',
    temsilci: 'temsilci',
    'icerik-seo': 'icerik',
    denetci: 'denetci',
});
/** The reverse of PANEL_AGENT_IDS: HTTP paths take the config id. */
export const CONFIG_AGENT_IDS = Object.freeze({
    gm: 'genel-mudur',
    temsilci: 'temsilci',
    icerik: 'icerik-seo',
    denetci: 'denetci',
});
/** Panel id of a config agent, or null when the panel does not show it. */
export function panelAgentIdOf(configAgentId) {
    return Object.hasOwn(PANEL_AGENT_IDS, configAgentId) ? (PANEL_AGENT_IDS[configAgentId] ?? null) : null;
}
/** Config id of a panel agent, for HTTP paths such as /api/v1/agents/{agentId}/pause. */
export function configAgentIdOf(panelAgentId) {
    return CONFIG_AGENT_IDS[panelAgentId];
}
/**
 * twin.approvals.reject_reason_code. already_done (v0.1.178, migration 0091): Sercan already sent or did it himself (an
 * Etsy reply or delivery Twin cannot read); not a verdict on the draft, so no rework run and no learning row.
 */
export const REJECT_REASON_CODES = [
    'wrong_info',
    'tone',
    'unnecessary',
    'timing',
    'policy',
    'other',
    'already_done',
];
export const rejectReasonCodeSchema = z.enum(REJECT_REASON_CODES);
export const VERDICT_REJECT_REASON_CODES = REJECT_REASON_CODES.filter((code) => code !== 'already_done');
/** Reason codes for rejecting a request to Sercan (twin.requests). */
export const REQUEST_REJECT_CODES = ['not_needed', 'not_now', 'wrong_info', 'other'];
export const requestRejectCodeSchema = z.enum(REQUEST_REJECT_CODES);
export const KILL_SWITCH_LEVELS = ['none', 'pause', 'stop'];
export const killSwitchLevelSchema = z.enum(KILL_SWITCH_LEVELS);
export const tierSchema = z.enum(['T3', 'T4']);
export const dataDomainSchema = z.enum(['shop', 'growth', 'system']);
/** twin.approvals.status. */
export const APPROVAL_STATUS_VALUES = ['pending', 'approved', 'rejected', 'expired', 'withdrawn', 'undone'];
export const approvalStatusSchema = z.enum(APPROVAL_STATUS_VALUES);
/** twin.approvals.execution_status (all ten database values). */
export const EXECUTION_STATUS_VALUES = [
    'none',
    'scheduled',
    'manual_pending',
    'dry_run',
    'executing',
    'executed',
    'verified',
    'failed',
    'manual_done',
    'cancelled',
];
export const executionStatusSchema = z.enum(EXECUTION_STATUS_VALUES);
/** twin.approvals.verifier_status. */
export const verifierStatusSchema = z.enum(['passed', 'rejected', 'not_checked']);
/** Length in Unicode code points: an emoji counts once, as in the bot ([...text].length) and Postgres length(). */
export function codePointLength(value) {
    return Array.from(value).length;
}
/**
 * A string of min..max characters counted in code points. zod's .min() and .max()
 * count UTF-16 code units, so an emoji would count twice there and a text the bot
 * accepted (its limits count code points) would be a 400 at the API.
 */
export function boundedText(min, max) {
    return z
        .string()
        .max(max * 2)
        .refine((value) => {
        const length = codePointLength(value);
        return length >= min && length <= max;
    }, { error: `expected ${min} to ${max} characters` });
}
//# sourceMappingURL=ids.js.map